import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { getTenantByDomain, TenantConfig } from '../config/tenants';

/**
 * Context para el sistema multi-tenant
 * Detecta automáticamente el dominio y aplica la configuración correspondiente
 */

interface TenantContextType {
  tenant: TenantConfig;
  isLoading: boolean;
}

const TenantContext = createContext<TenantContextType | null>(null);

interface TenantProviderProps {
  children: ReactNode;
}

/**
 * Provider del sistema multi-tenant
 * Debe envolver toda la aplicación en App.tsx
 */
export function TenantProvider({ children }: TenantProviderProps) {
  const [tenant, setTenant] = useState<TenantConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Detectar tenant por dominio
    const detectedTenant = getTenantByDomain();
    setTenant(detectedTenant);

    // Aplicar tema CSS
    applyTheme(detectedTenant);

    // Cargar fuentes de Google si son necesarias
    loadGoogleFonts(detectedTenant);

    setIsLoading(false);

    console.log(`🎨 Tenant detectado: ${detectedTenant.slug} (${detectedTenant.domain})`);
  }, []);

  // Mostrar loading mientras se carga el tenant
  if (isLoading || !tenant) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        fontSize: '1.5rem',
        fontFamily: 'system-ui, -apple-system, sans-serif'
      }}>
        <div>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🧠</div>
          <div>Cargando Neureka...</div>
        </div>
      </div>
    );
  }

  return (
    <TenantContext.Provider value={{ tenant, isLoading }}>
      {children}
    </TenantContext.Provider>
  );
}

/**
 * Hook para acceder a la configuración del tenant actual
 * @returns La configuración del tenant
 * @throws Error si se usa fuera del TenantProvider
 */
export function useTenant(): TenantConfig {
  const context = useContext(TenantContext);

  if (!context) {
    throw new Error('useTenant debe usarse dentro de TenantProvider');
  }

  return context.tenant;
}

/**
 * Hook para verificar si estamos en el tenant Kids
 */
export function useIsKidsTenant(): boolean {
  const tenant = useTenant();
  return tenant.slug === 'kids';
}

/**
 * Hook para verificar si estamos en el tenant Default
 */
export function useIsDefaultTenant(): boolean {
  const tenant = useTenant();
  return tenant.slug === 'default';
}

/**
 * Aplica el tema del tenant como CSS variables
 */
function applyTheme(tenant: TenantConfig): void {
  const root = document.documentElement;
  const { theme } = tenant;

  // Colores
  root.style.setProperty('--color-primary', theme.colors.primary);
  root.style.setProperty('--color-secondary', theme.colors.secondary);
  root.style.setProperty('--color-accent', theme.colors.accent);
  root.style.setProperty('--color-background', theme.colors.background || '#0F172A');
  root.style.setProperty('--gradient-main', theme.colors.gradients.main);
  root.style.setProperty('--gradient-header', theme.colors.gradients.header);
  root.style.setProperty('--gradient-card', theme.colors.gradients.card || 'none');

  // Fuentes
  root.style.setProperty('--font-primary', `'${theme.fonts.primary.family}', sans-serif`);
  root.style.setProperty('--font-body', `'${theme.fonts.body.family}', sans-serif`);
  root.style.setProperty('--font-decorative', theme.fonts.decorative ? `'${theme.fonts.decorative.family}', cursive` : `'${theme.fonts.primary.family}', sans-serif`);

  // Spacing
  root.style.setProperty('--button-padding', theme.spacing.buttonPadding);
  root.style.setProperty('--card-border-radius', theme.spacing.cardBorderRadius);
  root.style.setProperty('--header-height', theme.spacing.headerHeight);
  root.style.setProperty('--container-padding', theme.spacing.containerPadding || '1rem');

  // Effects
  root.style.setProperty('--blur-amount', theme.effects.blur);
  root.style.setProperty('--glassmorphism', theme.effects.glassmorphism ? 'blur(var(--blur-amount))' : 'none');

  // Shadow según configuración
  const shadowMap = {
    soft: '0 4px 12px rgba(0, 0, 0, 0.1)',
    medium: '0 8px 24px rgba(0, 0, 0, 0.15)',
    strong: '0 12px 32px rgba(0, 0, 0, 0.2)',
  };
  root.style.setProperty('--shadow-default', shadowMap[theme.effects.shadows || 'medium']);

  // Aplicar clase de animación al body
  document.body.className = `tenant-${tenant.slug} animations-${theme.effects.animations}`;

  console.log(`✅ Tema aplicado: ${tenant.name}`);
}

/**
 * Carga las fuentes de Google Fonts necesarias
 */
function loadGoogleFonts(tenant: TenantConfig): void {
  const { theme } = tenant;
  const fontsToLoad: string[] = [];

  // Recopilar fuentes que necesitan ser cargadas
  if (theme.fonts.primary.googleFont) {
    const weights = theme.fonts.primary.weights.join(';');
    fontsToLoad.push(`family=${theme.fonts.primary.family.replace(' ', '+')}:wght@${weights}`);
  }

  if (theme.fonts.body.googleFont && theme.fonts.body.family !== theme.fonts.primary.family) {
    const weights = theme.fonts.body.weights.join(';');
    fontsToLoad.push(`family=${theme.fonts.body.family.replace(' ', '+')}:wght@${weights}`);
  }

  if (theme.fonts.decorative?.googleFont && theme.fonts.decorative.family !== theme.fonts.primary.family) {
    const weights = theme.fonts.decorative.weights.join(';');
    fontsToLoad.push(`family=${theme.fonts.decorative.family.replace(' ', '+')}:wght@${weights}`);
  }

  if (fontsToLoad.length === 0) return;

  // Verificar si ya existe el link
  const existingLink = document.querySelector('link[data-tenant-fonts]');
  if (existingLink) {
    existingLink.remove();
  }

  // Crear link para Google Fonts
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?${fontsToLoad.join('&')}&display=swap`;
  link.setAttribute('data-tenant-fonts', 'true');
  document.head.appendChild(link);

  console.log(`📝 Fuentes cargadas: ${fontsToLoad.length}`);
}
